import * as React from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { Grid, Button, Modal, Title, Divider, Stack } from "@mantine/core";

import { CTextInput, CSelect, CMultiSelect, CRadioGroup, CTextarea } from "@supportinitiative/react-hook-form-mantine";

import { getSystemStatus, getConfirmationStatus } from "../../../UseCaseUtils";
import { object, string, array } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { UAction, UAnchorMatchActionType, UseCaseType } from "@supportinitiative/common";
import { UseCaseCVMContext } from "src/UseCase/UseCaseCVMContext";
import Query from "./Query";

const QuerySchema = object({
  columns: array().of(string()),
  additionalColumns: array().of(
    object({
      name: string().required(),
      type: string().required(),
      label: string().nullable(),
      genFunction: string().required(),
      id: string().nullable(),
    }),
  ),
  where: string().nullable(),
  having: string().nullable(),
  order: string().nullable(),
});

const AnchorMatchActionSchema = object({
  id: string().required(),
  name: string().required(),
  type: string().required().oneOf(["ANCHOR_MATCH"]),
  description: string().nullable(),
  definition: object({
    type: string().required(),
    left: QuerySchema,
    right: QuerySchema,
    anchorCondition: string().nullable(),
    matchCondition: string().nullable(),
    systemStatus: string().required(),
    confirmationStatus: string().required(),
    userStatus: array().of(string().required()),
    statusOverride: string().required().oneOf(["OVERRIDE", "APPEND", "IGNORE"]),
  }),
});

function getDatasetMatchLOV(recon?: UseCaseType["definition"]["recons"][number]) {
  if (recon) {
    if (recon.definition.datasets) {
      const matches = [];

      for (const dataset of recon.definition.datasets) {
        matches.push({
          value: `WITHIN-${dataset.id}`,
          label: `Within - ${dataset.id} [ ${dataset.name} ]`,
        });

        matches.push({
          value: `${dataset.id}-${dataset.id}`,
          label: `${dataset.id} [ ${dataset.name} ] - ${dataset.id} [ ${dataset.name} ]`,
        });

        for (const d of recon.definition.datasets) {
          if (d.id !== dataset.id) {
            matches.push({
              value: `${dataset.id}-${d.id}`,
              label: `${dataset.id} [ ${dataset.name} ] - ${d.id} [ ${d.name} ]`,
            });
          }
        }
      }

      return matches;
    } else {
      return [];
    }
  } else {
    return [];
  }
}

function getDatasetId(type: string, leftOrRight: "LEFT" | "RIGHT") {
  if (type) {
    if (type.toUpperCase().startsWith("WITHIN")) {
      return type.split("-")[1];
    } else {
      if (leftOrRight === "LEFT") {
        return type.split("-")[0];
      } else {
        return type.split("-")[1];
      }
    }
  }
  return "";
}

function AnchorMatch({
  recon,
  save,
  cancel,
  action,
}: {
  cancel: () => void;
  save: (values: any) => void;
  action: any;
  recon: UseCaseType["definition"]["recons"][number];
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UAction<UAnchorMatchActionType>>({
    defaultValues: {
      ...action,
      definition: {
        ...action.definition,
        statusOverride: action.definition.statusOverride || "IGNORE",
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(AnchorMatchActionSchema),
  });

  const type = useWatch({
    control,
    name: "definition.type",
  });

  return (
    <Modal centered size="xl" opened={true} onClose={cancel} title={<Title order={4}>Anchor Match</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          console.log("inside child");
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput name="id" control={control} label="ID" required showErrorText={false} disabled={true} />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              name="description"
              minLength={5}
              control={control}
              label="Description"
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Divider my="xs" label="Dataset" />

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name={`definition.type`}
              control={control}
              label={"Dataset"}
              data={getDatasetMatchLOV(recon)}
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Stack>
          <Divider my="xs" label="Query" labelPosition="center" />

          {type && (
            <>
              <Grid align="stretch" gutter={"xs"} mb="xs">
                <Grid.Col span={12}>
                  <Query
                    name={`definition.left`}
                    control={control}
                    label="Query - Left"
                    datasetId={getDatasetId(type, "LEFT")}
                    recon={recon}
                  />
                </Grid.Col>
              </Grid>

              {type && !type.toUpperCase().startsWith("WITHIN") && (
                <Grid align="stretch" gutter={"xs"} mb="xs">
                  <Grid.Col span={12}>
                    <Query
                      name={`definition.right`}
                      control={control}
                      label="Query - Right"
                      datasetId={getDatasetId(type, "RIGHT")}
                      recon={recon}
                    />
                  </Grid.Col>
                </Grid>
              )}
            </>
          )}

          <Divider my="xs" label="Anchor" labelPosition="center" />

          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextarea
                autosize
                minRows={4}
                name={`definition.anchorCondition`}
                control={control}
                label={"Anchor Condition"}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Divider my="xs" label="Match" labelPosition="center" />

          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextarea
                autosize
                minRows={4}
                name={`definition.matchCondition`}
                control={control}
                label={"Match Condition"}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Divider my="xs" label="Match ID Gen Function" labelPosition="center" />

          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CTextarea
                autosize
                minRows={4}
                name={`definition.matchIdGenFunction`}
                control={control}
                label={"Gen Function"}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Divider my="xs" label="Status" labelPosition="center" />

          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={4}>
              <CSelect
                name={`definition.systemStatus`}
                control={control}
                label="Status"
                data={getSystemStatus("ALL")}
                showErrorText={false}
                searchable
                required
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CSelect
                name={`definition.confirmationStatus`}
                control={control}
                label="Confirmation Status"
                data={getConfirmationStatus()}
                showErrorText={false}
                searchable
                required
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CMultiSelect
                name={`definition.userStatus`}
                control={control}
                label="User Status"
                searchable
                data={useCaseFormState.userStatus || []}
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Divider my="xs" label="Status Override" labelPosition="center" />

          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={12}>
              <CRadioGroup
                name={`definition.statusOverride`}
                control={control}
                data={[
                  { label: "Ignore", value: "IGNORE" },
                  /* { label: "Override", value: "OVERRIDE" }, */
                  { label: "Append", value: "APPEND" },
                ]}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </Stack>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                useCaseFormState.actionOptions.disableAll
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await AnchorMatchActionSchema.validate(values, {
                          abortEarly: false,
                        });
                        console.log("values", values);
                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}

export default AnchorMatch;
